import { Application } from '@platform/client';
import { ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { ApplicationRejected } from '../result/shared/ApplicationRejected';
import { ApplicationUnderReview } from '../result/shared/ApplicationUnderReview';
import { ApplicationWaitlisted } from '../result/shared/ApplicationWaitlisted';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';
import { PhilipsConfirmAndSubmit } from '../final-details/philips/PhilipsConfirmAndSubmit';
import LeftSectionImage from '../images/Default.png';
import { useApplicationLocalStorage } from '../application-hook';
import { REVIEW_MODE_STORAGE_KEY, ReviewModeStorage } from '../../dashboard/home/RejoinPanel';
import { PhilipsReferralSelection } from '../program/philips/PhilipsReferralSelection';
import { PROGRAM_NAMES } from '../../auth/account-verification/ApplicationStartPage';

enum PhilipsApplicationSteps {
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',
  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',
  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',
  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
  CONFIRM_AND_SUBMIT = 'CONFIRM_AND_SUBMIT',
}

const PhilipsApplicationStep: Record<PhilipsApplicationSteps, Step> = {
  [PhilipsApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/',
  },
  [PhilipsApplicationSteps.LOCATION_SELECTION]: {
    progress: 130,
    component: LocationSelection,
    url: '/location-selection',
  },
  [PhilipsApplicationSteps.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [PhilipsApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 160,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [PhilipsApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [PhilipsApplicationSteps.SKILLS_SELECTION]: {
    progress: 220,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [PhilipsApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 280,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [PhilipsApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 320,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [PhilipsApplicationSteps.REFERRAL_SELECTION]: {
    progress: 380,
    component: PhilipsReferralSelection,
    url: '/referral-selection',
  },
  [PhilipsApplicationSteps.CONFIRM_AND_SUBMIT]: {
    progress: 400,
    component: PhilipsConfirmAndSubmit,
    url: '/confirm-and-submit',
  },
} as const;

export const PhilipsApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const { getApplicationDataByKey: getReviewMode } =
    useApplicationLocalStorage<ReviewModeStorage>(REVIEW_MODE_STORAGE_KEY);
  const isReviewMode = getReviewMode('reviewMode');

  const goToNextStep = () => {
    const steps = Object.values(PhilipsApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return PhilipsApplicationStep[step as PhilipsApplicationSteps].url === location;
    });
    const nextStep = currentIndex < steps.length - 1 ? steps[currentIndex + 1] : location;
    const nextUrl = PhilipsApplicationStep[nextStep as PhilipsApplicationSteps].url;
    navigate(nextUrl);
    return nextStep as PhilipsApplicationSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(PhilipsApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => PhilipsApplicationStep[step as PhilipsApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=philips';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = PhilipsApplicationStep[previousStep as PhilipsApplicationSteps].url;
    navigate(previousUrl);
    return previousStep as PhilipsApplicationSteps;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.waitlisted) {
    return (
      <ApplicationWaitlisted
        programName={`the ${PROGRAM_NAMES['philips']} program`}
        backgroundImage={LeftSectionImage}
      />
    );
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    return (
      <ApplicationRejected
        programName={`${PROGRAM_NAMES['philips']} program`}
        backgroundImage={LeftSectionImage}
      />
    );
  }

  if (
    applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.approved &&
    !isReviewMode
  ) {
    return (
      <ApplicationApproved
        cohortKickoffDate={applicationData.data?.cohortKickoffDate}
        clientUrlParam={applicationData.data?.clientUrlParam}
      />
    );
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.pending) {
    return <ApplicationUnderReview />;
  }

  console.log('hitting wouter switch, no app status');

  return (
    <Switch>
      {Object.entries(PhilipsApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
